import {_} from 'lodash';
import * as ko from 'knockout';
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from '../../../constants';
import {PLEASE_WAIT_MESSAGE} from '../../../constants/Language';
import {IMessageData, IObjectData, IOverride} from '../../../index';
import {createErrorMessage, getObjectType, getSyncFieldValue} from '../../../util/misc';
import {getSearchObjectParams} from '../../../util/request';
import {global} from '../../../appContext';
import {memoryStore} from '../../../util/memorystore';
import PromiseRequest from '../../../req/PromiseRequest';
import {nsRuleset} from '../../ruleset/nsRuleset';
import {nsGroupMemberOverride} from '../../group/members/nsGroupMemberOverride';
import {nsGroupMembers} from '../../group/members/nsGroupMembers';
import {nsOverride} from '../../override/nsOverride';
import pubsub from '../../../pubsub';
import {MembershipRowVm, IUserMembershipListParent} from './membershipRow';
import {nsUserMembership} from './nsUserMembership';
import filterRelevantMembershipOverrides = nsUserMembership.filterRelevantMembershipOverrides;
import findOverrideForGroup = nsUserMembership.findOverrideForGroup;
import collectOverrideGroupSourceIds = nsUserMembership.collectOverrideGroupSourceIds;
import mergeMembershipGroups = nsUserMembership.mergeMembershipGroups;
import getMembershipOverrideSortRank = nsUserMembership.getMembershipOverrideSortRank;
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;
import getMembershipOverrideAction = nsGroupMemberOverride.getMembershipOverrideAction;
import runMembershipOverrideDelete = nsGroupMemberOverride.runMembershipOverrideDelete;
import runMembershipOverrideRestore = nsGroupMemberOverride.runMembershipOverrideRestore;
import runMembershipRemoveMember = nsGroupMemberOverride.runMembershipRemoveMember;
import showCancelPendingDeletionConfirm = nsGroupMemberOverride.showCancelPendingDeletionConfirm;
import showMembershipOverrideActionConfirm = nsGroupMemberOverride.showMembershipOverrideActionConfirm;
import invalidateMembershipOverrideCaches = nsGroupMemberOverride.invalidateMembershipOverrideCaches;
import IMembershipOverrideMutationCallbacks = nsGroupMemberOverride.IMembershipOverrideMutationCallbacks;
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import generateRule = nsRuleset.generateRule;
import generateRuleset = nsRuleset.generateRuleset;

export type TMembershipSortColumn = 'Name' | 'Description' | 'override';

/** Loads user memberships, merges override-only groups, and drives the membership grid. */
export class MembershipList implements IUserMembershipListParent {
    readonly isReady = ko.observable(false);
    readonly isRefreshing = ko.observable(false);
    readonly messageData = ko.observable<IMessageData>(null);
    readonly membershipOverrides = ko.observableArray<IOverride>([]);
    readonly sortcol = memoryStore.get('user.membership.sortcolumn', 'Name');
    readonly sortascending = memoryStore.get('user.membership.direction', true);

    private userData: IObjectData | null = null;
    private groups: IObjectData[] = [];
    private readonly rowByGroupId = new Map<number, MembershipRowVm>();

    readonly memberRows: KnockoutComputed<MembershipRowVm[]>;
    readonly hasMemberRows: KnockoutComputed<boolean>;
    /** Same count as rendered grid rows (`memberof` + override-only groups). */
    readonly membershipCount: KnockoutComputed<number>;
    readonly memberActionsEnabled = ko.pureComputed(() => !this.isRefreshing());

    private readonly reqGetObject = new PromiseRequest('getobject');
    private readonly reqSearchGroups = new PromiseRequest('searchobject');
    private readonly reqCreateOverride = new PromiseRequest('createoverride');
    private readonly reqDeleteOverride = new PromiseRequest('deleteoverride');
    private readonly reqRestoreOverride = new PromiseRequest('restoreoverride');
    private readonly reqUpdateOverrideComment = new PromiseRequest('updateoverridecomment');

    constructor(readonly userId: number) {
        this.memberRows = ko.pureComputed(() => this.resolveSortedRows());
        this.hasMemberRows = ko.pureComputed(() => this.memberRows().length > 0);
        this.membershipCount = ko.pureComputed(() => this.memberRows().length);
        pubsub.on(this, 'object.needs.refresh', this.onObjectNeedsRefresh);
    }

    /** Starts initial load with please-wait messaging. */
    start(): void {
        this.isReady(false);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        void this.loadUser();
    }

    getUserData(): IObjectData | null {
        return this.userData;
    }

    /** Group ids already shown in the membership list (synced and override-only). */
    getExcludedGroupIds(): number[] {
        return this.groups
            .map(g => g.id)
            .filter((id): id is number => id != null);
    }

    getOverrideForGroup(group: IObjectData): IOverride | null {
        return findOverrideForGroup(this.membershipOverrides(), group);
    }

    /** Plain row action (cancel / restore / remove) for one group membership. */
    onGroupRowActionClick(group: IObjectData): void {
        this.onGroupActionClick(group);
    }

    refreshAfterOverrideMutation(): Promise<void> {
        return this.reload(true);
    }

    dispose(): void {
        pubsub.off(this, 'object.needs.refresh');
        this.reqGetObject.abort();
        this.reqSearchGroups.abort();
        this.reqCreateOverride.abort();
        this.reqDeleteOverride.abort();
        this.reqRestoreOverride.abort();
        this.reqUpdateOverrideComment.abort();
    }

    private onObjectNeedsRefresh = (): void => {
        if (this.isReady()) {
            void this.refreshAfterOverrideMutation();
        }
    };

    private async loadUser(): Promise<void> {
        try {
            const data = await this.reqGetObject.request({id: this.userId, manual: false}) as [IObjectData];
            this.userData = data[0];
            await this.applyUserData(this.userData, false);
            this.messageData(null);
            this.isReady(true);
        } catch (reason: unknown) {
            this.messageData(createErrorMessage(String(reason)));
        }
    }

    private async reload(dimGrid: boolean): Promise<void> {
        if (!this.userId) {
            return;
        }
        if (dimGrid) {
            this.isRefreshing(true);
        }
        try {
            invalidateMembershipOverrideCaches();
            const data = await this.reqGetObject.request({id: this.userId, manual: false}) as [IObjectData];
            this.userData = data[0];
            await this.applyUserData(this.userData, dimGrid);
            this.messageData(null);
        } catch (reason: unknown) {
            this.messageData(createErrorMessage(String(reason)));
        } finally {
            this.isRefreshing(false);
        }
    }

    private async applyUserData(user: IObjectData, _dimGrid: boolean): Promise<void> {
        const memberof = user?.memberof ?? [];
        const overrides = filterRelevantMembershipOverrides(user?.overrides ?? [], this.userId);

        let extraGroups: IObjectData[] = [];
        if (global.permissionValidator.isMinCompanyAdmin()) {
            extraGroups = await this.fetchOverrideOnlyGroups(overrides, memberof);
        }

        this.groups = mergeMembershipGroups(memberof, extraGroups);
        this.pruneRowCache();
        this.membershipOverrides(overrides);
    }

    private async fetchOverrideOnlyGroups(
        overrides: IOverride[],
        memberof: IObjectData[],
    ): Promise<IObjectData[]> {
        const sourceIds = collectOverrideGroupSourceIds(overrides, memberof);
        if (!sourceIds.length) {
            return [];
        }
        const ruleset = generateRuleset({
            companyid: global.login.selectedOrganizationId() ?? '',
            objecttype: getObjectType(ObjectTypeId.Group),
            rules: [generateRule({
                argument: sourceIds.join(','),
                argumentoperator: 'Er lik en av',
                attribute: 'SourceId',
                order: 1,
            })],
        });
        try {
            return await this.reqSearchGroups.request(getSearchObjectParams({
                resultsprpage: MAX_RESULTS_PER_PAGE,
                jruleset: JSON.stringify(ruleset),
                objecttypeid: ObjectTypeId.Group,
            })) as IObjectData[];
        } catch {
            return [];
        }
    }

    private onGroupActionClick(group: IObjectData): void {
        if (!this.userData || !global.permissionValidator.isMinCompanyAdmin()) {
            return;
        }
        const override = this.getOverrideForGroup(group);
        if (override) {
            if (isOverridePendingDeletion(override)) {
                showCancelPendingDeletionConfirm(override, () =>
                    this.executeRestoreOverride(group, override));
                return;
            }
            if (!canMutateMembershipOverride(override)) {
                return;
            }
            showMembershipOverrideActionConfirm(override, () =>
                this.executeDeleteOverride(group, override));
            return;
        }
        nsGroupMembers.showManualMembershipEducationConfirm(() => this.executeRemoveMember(group));
    }

    private executeDeleteOverride(group: IObjectData, override: IOverride): void {
        // Reused rows keep expand state across refresh — collapse before restore.
        if (getMembershipOverrideAction(override).outcome === 'restore' && group.id != null) {
            this.rowByGroupId.get(group.id)?.isExpanded(false);
        }
        this.runMutation(callbacks =>
            runMembershipOverrideDelete(this.reqDeleteOverride, override, callbacks));
    }

    private executeRestoreOverride(group: IObjectData, override: IOverride): void {
        if (group.id != null) {
            this.rowByGroupId.get(group.id)?.isExpanded(false);
        }
        this.runMutation(callbacks =>
            runMembershipOverrideRestore(this.reqRestoreOverride, override, callbacks));
    }

    private executeRemoveMember(group: IObjectData): void {
        this.runMutation(callbacks =>
            runMembershipRemoveMember(this.reqCreateOverride, group, this.userData!, callbacks));
    }

    updateOverrideComment(overrideId: number, comment: string): Promise<void> {
        return this.reqUpdateOverrideComment
            .request({id: overrideId, comment})
            .then(() => this.refreshAfterOverrideMutation())
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                return Promise.reject(reason);
            });
    }

    private runMutation(
        run: (callbacks: IMembershipOverrideMutationCallbacks) => void,
    ): void {
        this.isRefreshing(true);
        run({
            onStart: message => this.messageData({message}),
            onSuccess: () => {
                this.messageData(null);
                void this.refreshAfterOverrideMutation().finally(() => this.isRefreshing(false));
            },
            onError: reason => {
                this.messageData(reason);
                this.isRefreshing(false);
            },
            onFinally: () => {},
        });
    }

    private resolveSortedRows(): MembershipRowVm[] {
        const overrides = this.membershipOverrides();
        const ascending = this.sortascending();
        const col = this.normalizeSortColumn(this.sortcol());
        const sorted = _.sortBy(this.groups, [
            (g: IObjectData) => this.getSortValue(col, g, overrides, ascending),
            (g: IObjectData) => (getSyncFieldValue('Name', g.syncfields) ?? '').toLowerCase(),
            'id',
        ]);
        const ordered = ascending ? sorted : sorted.reverse();
        const rows: MembershipRowVm[] = [];
        const activeIds = new Set<number>();

        for (const group of ordered) {
            if (group.id == null) {
                continue;
            }
            activeIds.add(group.id);
            let row = this.rowByGroupId.get(group.id);
            if (!row) {
                row = new MembershipRowVm(group, this);
                this.rowByGroupId.set(group.id, row);
            } else {
                row.setGroup(group);
            }
            rows.push(row);
        }

        for (const id of Array.from(this.rowByGroupId.keys())) {
            if (!activeIds.has(id)) {
                this.rowByGroupId.delete(id);
            }
        }

        return rows;
    }

    /** Maps legacy `id` sort column from the old membership tab to `Name`. */
    private normalizeSortColumn(col: string): TMembershipSortColumn {
        if (col === 'Description' || col === 'override') {
            return col;
        }
        return 'Name';
    }

    private getSortValue(
        col: TMembershipSortColumn,
        group: IObjectData,
        overrides: IOverride[],
        ascending: boolean,
    ): string | number {
        switch (col) {
            case 'Name':
                return (getSyncFieldValue('Name', group.syncfields) ?? '').toLowerCase();
            case 'Description':
                return (getSyncFieldValue('Description', group.syncfields) ?? '').toLowerCase();
            case 'override':
                return getMembershipOverrideSortRank(findOverrideForGroup(overrides, group), ascending);
            default:
                return '';
        }
    }

    private pruneRowCache(): void {
        const ids = new Set(this.groups.map(g => g.id).filter((id): id is number => id != null));
        for (const id of Array.from(this.rowByGroupId.keys())) {
            if (!ids.has(id)) {
                this.rowByGroupId.delete(id);
            }
        }
    }
}
